import { formatDateLabel, formatOrdinalDateTimeInTimeZone } from '../../../utils/dateFormat';
import type { ReportFilters, ReportValueFormat } from '../../../types/reports';

export const CURRENCY = '৳';

/** Tokens that should stay upper-cased when humanising status labels (ICU, OPD, VIP…). */
const ACRONYMS = new Set([
  'ICU',
  'CCU',
  'NICU',
  'OPD',
  'IPD',
  'ER',
  'ENT',
  'MRI',
  'CT',
  'USG',
  'ECG',
  'CBC',
  'LFT',
  'RFT',
  'UPI',
  'HIV',
  'VIP',
  'MRN',
  'HR',
]);

export function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function formatCurrency(value: unknown): string {
  const amount = Number(value) || 0;
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Accounting convention: the sign precedes the currency symbol.
  return `${amount < 0 ? '-' : ''}${CURRENCY}${formatted}`;
}

export function formatCell(value: unknown, format?: ReportValueFormat, timezone?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (format) {
    case 'currency': {
      const amount = Number(value);
      return Number.isFinite(amount) ? formatCurrency(amount) : String(value);
    }
    case 'number': {
      const amount = Number(value);
      return Number.isFinite(amount) ? amount.toLocaleString('en-US') : String(value);
    }
    case 'date':
      return formatDateLabel(String(value));
    case 'datetime':
      return formatOrdinalDateTimeInTimeZone(String(value), timezone);
    case 'percent': {
      const amount = Number(value);
      return Number.isFinite(amount) ? `${amount.toLocaleString('en-US')}%` : String(value);
    }
    default:
      return String(value);
  }
}

/** Renders a breakdown item using the format the backend declared for the group. */
export function formatBreakdownValue(value: number, format?: ReportValueFormat): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  if (format === 'currency') return formatCurrency(amount);
  if (format === 'percent') return `${amount.toLocaleString('en-US')}%`;
  return amount.toLocaleString('en-US');
}

/** Human-readable labels for the filters the backend actually applied to a result. */
export function describeFilters(filters: ReportFilters): string[] {
  const parts: string[] = [];
  if (filters.dateFrom || filters.dateTo) {
    parts.push(`Period: ${filters.dateFrom || 'start'} → ${filters.dateTo || 'today'}`);
  }
  if (filters.dateBasis) {
    parts.push(filters.dateBasis === 'settled' ? 'Basis: Settled On' : 'Basis: Document date');
  }
  if (filters.departmentName) parts.push(`Department: ${filters.departmentName}`);
  if (filters.doctorName) parts.push(`Doctor: ${filters.doctorName}`);
  if (filters.patientName) parts.push(`Patient: ${filters.patientName}`);
  if (filters.categoryName) parts.push(`Category: ${filters.categoryName}`);
  if (filters.serviceTypeName) parts.push(`Type: ${filters.serviceTypeName}`);
  if (filters.diagnosticCategoryName) {
    parts.push(`Diagnostic category: ${filters.diagnosticCategoryName}`);
  }
  return parts;
}

/**
 * Normalises the error object produced by the axios response interceptor
 * (`{ code, message, details }`). Nest's global exception filter collapses
 * class-validator output into a generic "Validation failed" message and moves
 * the real reasons into `error[].reason`, so we surface those when present.
 */
export function describeError(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;
  const err = error as { message?: unknown; details?: unknown };
  const details = Array.isArray(err.details)
    ? err.details
        .map((detail) =>
          detail && typeof detail === 'object' ? (detail as { reason?: unknown }).reason : detail,
        )
        .filter((reason): reason is string => typeof reason === 'string' && reason.length > 0)
    : [];
  const message = typeof err.message === 'string' ? err.message : '';
  if ((!message || message === 'Validation failed' || message === 'An error occurred') && details.length) {
    return details.join(', ');
  }
  return message || details.join(', ') || fallback;
}

export type Tone = 'success' | 'warning' | 'danger' | 'neutral';

const DANGER_STATUSES = new Set([
  'CANCEL',
  'CANCELLED',
  'ABSENT',
  'OVERDUE',
  'FAILED',
  'MAINTENANCE',
  'REJECT',
  'REJECTED',
  'NO_SHOW',
  'NOSHOW',
  'INACTIVE',
  'EXPIRED',
  'VOID',
  'REFUNDED',
]);

const SUCCESS_STATUSES = new Set([
  'COMPLETED',
  'PAID',
  'PRESENT',
  'SETTLED',
  'ACTIVE',
  'ADMITTED',
  'APPROVED',
  'AVAILABLE',
  'RECEIVED',
  'DONE',
  'DELIVERED',
  'CHECKED_IN',
  'FINALIZED',
]);

const WARNING_STATUSES = new Set([
  'PENDING',
  'ORDERED',
  'DRAFT',
  'PARTIAL',
  'PARTIALLY',
  'LATE',
  'RESERVED',
  'REPORT_PENDING',
  'IN_PROGRESS',
  'SCHEDULED',
  'PROCESSING',
  'HALF_DAY',
  'DISCHARGE_INITIATED',
  'TRANSFERRED',
  'COLLECTED',
  'ISSUED',
  'ON_LEAVE',
  'USED',
  'POSTED',
  'YES',
  'URGENT',
  'OCCUPIED',
]);

/**
 * Maps a status-like value to a colour tone. Matching is done on whole
 * underscore/space separated tokens so that substrings do not leak through
 * (e.g. "UNPAID" must not be treated as "PAID").
 */
export function statusTone(value: string): Tone {
  const v = value.toUpperCase();
  const tokens = v.split(/[^A-Z0-9]+/).filter(Boolean);
  const matches = (set: Set<string>) => set.has(v) || tokens.some((token) => set.has(token));

  if (matches(DANGER_STATUSES)) return 'danger';
  if (matches(SUCCESS_STATUSES)) return 'success';
  if (matches(WARNING_STATUSES)) return 'warning';
  return 'neutral';
}

export const TONE_CLASSES: Record<Tone, string> = {
  success: 'bg-success/10 text-success border-success/20',
  warning: 'bg-warning/10 text-warning border-warning/20',
  danger: 'bg-danger/10 text-danger border-danger/20',
  neutral: 'bg-background text-text-muted border-border',
};
